/**
 * 📊 V208.0: GDPR Cookie Consent Banner
 * 
 * Displays a cookie consent banner on web builds only.
 * Blocks PostHog tracking until user accepts.
 */

import { useState, useEffect } from 'react';
import { IS_WEB } from '@/config/channel';
import { enablePostHog } from '@/analytics';
// V209.12: Removed Button import to avoid pulling Radix UI into /auth bundle

const CONSENT_KEY = 'posthog_consent';

export function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Only show on web
    if (!IS_WEB) return;

    // Valid accepted/declined decisions persist until storage clear or consent-key version change
    try {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent === 'accepted' || consent === 'declined') {
        setShowBanner(false);
        return;
      }

      // Show banner if missing, malformed, or unknown
      setShowBanner(true);
    } catch (error) {
      console.warn('📊 V208.0: Failed to check consent status:', error);
      // If we can't check, don't show banner (fail-safe)
      setShowBanner(false);
    }
  }, []);

  const handleAccept = async () => {
    try {
      localStorage.setItem(CONSENT_KEY, 'accepted');
      localStorage.setItem(`${CONSENT_KEY}_date`, new Date().toISOString());
      await enablePostHog();
      setShowBanner(false);
    } catch (error) {
      console.warn('📊 V208.0: Failed to save consent:', error);
      // Still enable PostHog even if localStorage fails
      await enablePostHog();
      setShowBanner(false);
    }
  };

  const handleDecline = () => {
    try {
      localStorage.setItem(CONSENT_KEY, 'declined');
      localStorage.setItem(`${CONSENT_KEY}_date`, new Date().toISOString());
      setShowBanner(false);
    } catch (error) {
      console.warn('📊 V208.0: Failed to save consent decline:', error);
      setShowBanner(false);
    }
  };

  // Don't render on Android
  if (!IS_WEB || !showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-8 left-4 right-4 w-auto z-50 bg-background rounded-xl border border-border shadow-lg p-4 md:p-6">
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-1">
          <h2 className="font-semibold text-sm md:text-base mb-2">
            Cookie Consent
          </h2>
          <p className="text-xs md:text-sm text-muted-foreground">
            We use cookies and analytics to improve your experience and understand how you use BlissNet.
            By accepting, you agree to our use of analytics cookies. You can decline, but some features
            may not work optimally.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {/* V209.12: Plain HTML buttons (no Radix UI) to avoid bundling on /auth */}
          <button
            onClick={handleDecline}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs md:text-sm font-medium px-3 py-2 min-h-[44px] min-w-[44px] border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs md:text-sm font-medium px-3 py-2 min-h-[44px] min-w-[44px] bg-blue-600 text-white hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
